/**
 * 送信設定の編集画面（接続設定タブからドリルダウンして開く）。
 *
 * 2 つのモードで使い回す:
 *   - target.kind === "default" … テナントのデフォルト送信設定を編集
 *   - target.kind === "client"  … Client ID 個別の送信設定を編集
 *
 * Client ID 個別編集では各項目に「デフォルト設定に従う」を用意し、
 * 選ばなかった項目はデフォルトに追従する（部分上書き）。
 * こうしておくと、あとでデフォルトを直したときに個別設定側も自動で追いつく。
 */
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { card, labelStyle, inputStyle, btnPrimary, btnSecondary } from "./ui.js";
import ObjectOverrideBlocks from "./ObjectOverrideBlocks.js";

export type SendSettingsTarget =
  | { kind: "default" }
  | { kind: "client"; id: string; label: string };

type SendMode = "draft" | "direct" | "choose";
type TransferPolicy = "allow" | "deny" | "choose";

/** 「デフォルトに従う」を表す UI 専用の値 */
const INHERIT = "__inherit__";

interface Resolved {
  sendMode: SendMode;
  transferPolicy: TransferPolicy;
  honorificEnabled: boolean;
  honorificSuffix: string;
}

const SEND_MODE_OPTIONS: Array<{ value: SendMode; label: string; desc: string }> = [
  {
    value: "draft",
    label: "下書きモード（推奨）",
    desc: "クラウドサインに下書きを作成し、URLをHubSpotに格納します。クラウドサイン上で内容確認・署名後に送付してください。",
  },
  {
    value: "direct",
    label: "直接送信モード",
    desc: "クラウドサインに書類を作成後、即座に宛先に送信します。送信者の署名が不要な書類でのみ利用可能です。",
  },
  {
    value: "choose",
    label: "書類ごとに選択",
    desc: "送信モーダルで書類ごとに下書き/直接送信を選択できます。デフォルトは下書きです。",
  },
];

const TRANSFER_OPTIONS: Array<{ value: TransferPolicy; label: string; desc: string }> = [
  {
    value: "deny",
    label: "常に許可しない（推奨）",
    desc: "すべての書類で転送を許可しません。クラウドサインのデフォルト動作と同じです。",
  },
  {
    value: "allow",
    label: "常に許可する",
    desc: "すべての書類で転送を許可します。受信者側で「別の担当者に転送」ボタンが表示されます。",
  },
  {
    value: "choose",
    label: "書類ごとに選択",
    desc: "送信モーダルで書類ごとに転送の許可/不許可を選択できます。デフォルトは不許可です。",
  },
];

/** ラジオ 1 件分。選択中は枠を紫にする（既存の設定 UI と同じ見た目） */
function RadioCard({
  name,
  value,
  checked,
  label,
  desc,
  onChange,
}: {
  name: string;
  value: string;
  checked: boolean;
  label: string;
  desc: string;
  onChange: (value: string) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: 12,
        padding: "14px 16px",
        border: checked ? "2px solid #534AB7" : "1px solid #e8e7e3",
        borderRadius: 10,
        cursor: "pointer",
        background: checked ? "#FAFAFE" : "white",
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={(e) => onChange(e.target.value)}
        style={{ marginTop: 2 }}
      />
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, color: "#6b6b68", lineHeight: 1.6 }}>{desc}</div>
      </div>
    </label>
  );
}

export default function SendSettingsView({
  target,
  onBack,
}: {
  target: SendSettingsTarget;
  onBack: () => void;
}) {
  const isClient = target.kind === "client";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** テナントのデフォルト設定（Client ID 編集時は「継承したときの値」の表示に使う） */
  const [defaults, setDefaults] = useState<Resolved>({
    sendMode: "draft",
    transferPolicy: "deny",
    honorificEnabled: false,
    honorificSuffix: "様",
  });

  /** Client ID 編集時のみ: デフォルト継承か個別設定か */
  const [useCustom, setUseCustom] = useState(false);

  // フォームの値。Client ID 編集時は INHERIT を取り得る
  const [sendMode, setSendMode] = useState<SendMode | typeof INHERIT>("draft");
  const [transferPolicy, setTransferPolicy] = useState<TransferPolicy | typeof INHERIT>("deny");
  const [honorificMode, setHonorificMode] = useState<"on" | "off" | typeof INHERIT>("off");
  const [honorificSuffix, setHonorificSuffix] = useState("様");

  useEffect(() => {
    (async () => {
      try {
        const me = await api.getMe();
        const s = me?.settings || {};
        const base: Resolved = {
          sendMode: (s.sendMode as SendMode) || "draft",
          transferPolicy: (s.transferPolicy as TransferPolicy) || "deny",
          honorificEnabled: !!s.honorificEnabled,
          honorificSuffix: s.honorificSuffix || "様",
        };
        setDefaults(base);

        if (!isClient) {
          setSendMode(base.sendMode);
          setTransferPolicy(base.transferPolicy);
          setHonorificMode(base.honorificEnabled ? "on" : "off");
          setHonorificSuffix(base.honorificSuffix);
        } else {
          const data = await api.getClientIds();
          const row = (data.clientIds || []).find((c: any) => c.id === target.id);
          const ss = row?.sendSettings as Record<string, any> | null | undefined;
          setUseCustom(!!ss);
          setSendMode(ss?.sendMode ?? INHERIT);
          setTransferPolicy(ss?.transferPolicy ?? INHERIT);
          setHonorificMode(
            ss?.honorificEnabled === undefined ? INHERIT : ss.honorificEnabled ? "on" : "off",
          );
          setHonorificSuffix(ss?.honorificSuffix ?? base.honorificSuffix);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
    // target が変わったら再読込
  }, [target.kind, isClient ? target.id : ""]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      if (!isClient) {
        await api.updateMe({
          sendMode: sendMode as SendMode,
          transferPolicy: transferPolicy as TransferPolicy,
          honorificEnabled: honorificMode === "on",
          honorificSuffix: honorificSuffix.trim() || "様",
        });
      } else if (!useCustom) {
        // 個別設定を破棄してデフォルト継承に戻す
        await api.updateClientId(target.id, { sendSettings: null });
      } else {
        // INHERIT の項目はキーごと送らない = デフォルトに追従させる
        const payload: Record<string, unknown> = {};
        if (sendMode !== INHERIT) payload.sendMode = sendMode;
        if (transferPolicy !== INHERIT) payload.transferPolicy = transferPolicy;
        if (honorificMode !== INHERIT) {
          payload.honorificEnabled = honorificMode === "on";
          // 付与する場合のみ文字列も保存する
          if (honorificMode === "on") payload.honorificSuffix = honorificSuffix.trim() || "様";
        }
        // 全項目が「デフォルトに従う」なら継承と同じなので、個別設定を持たせない。
        // 空オブジェクトを残すと一覧のバッジ（デフォルト表示）と編集画面のラジオ
        // （個別設定）が食い違うため、null に寄せて状態を 1 つにする。
        const isEmpty = Object.keys(payload).length === 0;
        await api.updateClientId(target.id, { sendSettings: isEmpty ? null : (payload as any) });
        if (isEmpty) setUseCustom(false);
      }
      setSaved(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;
  }

  // 「デフォルトに従う」を選んだときに実際どうなるかを併記する
  const inheritDesc = (label: string) => `デフォルト設定の内容を使います（現在: ${label}）`;
  const defaultSendModeLabel =
    SEND_MODE_OPTIONS.find((o) => o.value === defaults.sendMode)?.label ?? defaults.sendMode;
  const defaultTransferLabel =
    TRANSFER_OPTIONS.find((o) => o.value === defaults.transferPolicy)?.label ?? defaults.transferPolicy;
  const defaultHonorificLabel = defaults.honorificEnabled
    ? `付与する（「${defaults.honorificSuffix}」）`
    : "付与しない";

  // 個別設定モードでない Client ID では各項目を編集させない
  const showFields = !isClient || useCustom;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <button onClick={onBack} style={{ ...btnSecondary, fontSize: 14, padding: "8px 18px" }}>
          ← 戻る
        </button>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {isClient ? `送信設定 — ${target.label}` : "デフォルト設定"}
          </div>
          <div style={{ fontSize: 13, color: "#6b6b68", marginTop: 2 }}>
            {isClient
              ? "このクライアントIDで送信するときの挙動を設定します。"
              : "個別設定を持たないすべてのクライアントIDに適用されます。"}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {isClient && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
            送信の挙動（送信モード・転送設定・敬称）
          </div>
          <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 14 }}>
            この3項目をデフォルト設定から引き継ぐか、このクライアントIDで個別に決めるかを選びます。
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <RadioCard
              name="useCustom"
              value="inherit"
              checked={!useCustom}
              label="デフォルト設定を使用"
              desc="デフォルト設定をそのまま使います。デフォルトを変更すると自動で反映されます。"
              onChange={() => setUseCustom(false)}
            />
            <RadioCard
              name="useCustom"
              value="custom"
              checked={useCustom}
              label="このクライアントIDで個別に設定"
              desc="項目ごとにデフォルトを上書きできます。上書きしなかった項目はデフォルトに追従します。"
              onChange={() => setUseCustom(true)}
            />
          </div>
        </div>
      )}

      {showFields && (
        <>
          {/* 送信モード */}
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>送信モード</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {isClient && (
                <RadioCard
                  name="sendMode"
                  value={INHERIT}
                  checked={sendMode === INHERIT}
                  label="デフォルト設定に従う"
                  desc={inheritDesc(defaultSendModeLabel)}
                  onChange={() => setSendMode(INHERIT)}
                />
              )}
              {SEND_MODE_OPTIONS.map((opt) => (
                <RadioCard
                  key={opt.value}
                  name="sendMode"
                  value={opt.value}
                  checked={sendMode === opt.value}
                  label={opt.label}
                  desc={opt.desc}
                  onChange={(v) => setSendMode(v as SendMode)}
                />
              ))}
            </div>
            <div style={{ marginTop: 12, padding: "10px 14px", background: "#FAEEDA", borderRadius: 8, fontSize: 13, color: "#854F0B", lineHeight: 1.6 }}>
              ⚠️ 直接送信モードでは、送信者の署名が不要な書類のみ正常に動作します。署名が必要な書類は下書きモードをご利用ください。
            </div>
          </div>

          {/* 書類の転送設定 */}
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>書類の転送設定</div>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 14, lineHeight: 1.6 }}>
              宛先の受信者が、受け取った書類を別の担当者へ転送できるかを決めます。クラウドサイン API の
              {" "}<code style={{ padding: "1px 6px", background: "#f1efe8", borderRadius: 4 }}>can_transfer</code>{" "}
              フィールドに連動します。
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {isClient && (
                <RadioCard
                  name="transferPolicy"
                  value={INHERIT}
                  checked={transferPolicy === INHERIT}
                  label="デフォルト設定に従う"
                  desc={inheritDesc(defaultTransferLabel)}
                  onChange={() => setTransferPolicy(INHERIT)}
                />
              )}
              {TRANSFER_OPTIONS.map((opt) => (
                <RadioCard
                  key={opt.value}
                  name="transferPolicy"
                  value={opt.value}
                  checked={transferPolicy === opt.value}
                  label={opt.label}
                  desc={opt.desc}
                  onChange={(v) => setTransferPolicy(v as TransferPolicy)}
                />
              ))}
            </div>
          </div>

          {/* 受信者氏名の敬称 */}
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>受信者氏名の敬称</div>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 14, lineHeight: 1.6 }}>
              関連コンタクトを送信モーダルの宛先に追加した際、氏名末尾に敬称を自動で付与します。
              宛先編集モーダルで個別に削除・編集することも可能です。
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {isClient && (
                <RadioCard
                  name="honorific"
                  value={INHERIT}
                  checked={honorificMode === INHERIT}
                  label="デフォルト設定に従う"
                  desc={inheritDesc(defaultHonorificLabel)}
                  onChange={() => setHonorificMode(INHERIT)}
                />
              )}
              <RadioCard
                name="honorific"
                value="on"
                checked={honorificMode === "on"}
                label="氏名末尾に敬称を自動付与する"
                desc="既に敬称が付いている場合はそのまま維持します。"
                onChange={() => setHonorificMode("on")}
              />
              <RadioCard
                name="honorific"
                value="off"
                checked={honorificMode === "off"}
                label="付与しない"
                desc="関連コンタクトの氏名がそのまま宛先に入ります。"
                onChange={() => setHonorificMode("off")}
              />
            </div>

            {honorificMode === "on" && (
              <div style={{ marginTop: 16 }}>
                <div style={labelStyle}>付与する文字列</div>
                <input
                  style={{ ...inputStyle, maxWidth: 240 }}
                  type="text"
                  maxLength={20}
                  value={honorificSuffix}
                  placeholder="様"
                  onChange={(e) => setHonorificSuffix(e.target.value)}
                />
                <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 6, lineHeight: 1.6 }}>
                  例: 「様」「御中」「殿」など。最大 20 文字。氏名と敬称の間にスペースは入りません
                  （例:「山田太郎様」）。
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, marginBottom: 16 }}>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
          {saving ? "保存中..." : "送信の挙動を保存"}
        </button>
        {saved && <span style={{ fontSize: 14, color: "#0F6E56", fontWeight: 600 }}>保存しました</span>}
      </div>

      {/*
        「誰に・何を送るか」は保存先が別（ClientSendOverride テーブル）なので
        保存ボタンも分ける。1つにまとめると、Tenant.settings 側と別トランザクションに
        なるため原子性を作れず、部分失敗が起きる。
      */}
      {/* デフォルト側は clientIdId = null。フォームは同じものを使い回す */}
      <ObjectOverrideBlocks clientIdId={isClient ? target.id : null} />

      <div style={{ marginTop: 8 }}>
        <button style={btnSecondary} onClick={onBack}>← 一覧に戻る</button>
      </div>
    </>
  );
}
